
import { defineComponent, reactive, watch } from 'vue';
import Icone from '@/core/components/Icone.vue';
import { useTelaBase } from '@/core/composables/TelaBase';
import { useModalBase } from '@/core/composables/ModalBase';
import { ITelaOperacao } from '@/core/models/ITelaOperacao';
import RequisicaoModal from '@/core/components/Modal/RequisicaoModal.vue';
import Card from '@/core/components/Tela/Card.vue';
import { ETipoPermissao } from '@/models/Enumeradores/MeuSistema/Usuarios/ETipoPermissao';
import { EPermissaoDados } from '@/models/Enumeradores/MeuSistema/Usuarios/EPermissaoDados';
import { IBanco } from '@/models/Entidades/Cadastros/Financeiro/IBanco';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import storeSistema from '@/store/storeSistema';
import ServicoPAS from '@/servicos/ServicoPAS';
import ServicoSistema from '@/servicos/MeuSistema/ServicoSistema';
import ServicoBanco from '@/servicos/Cadastros/Financeiro/ServicoBanco';
import { EStatusCuradoriaCadastro } from '@/models/Enumeradores/PainelAdministrativo/EStatusCuradoriaCadastro';
import { ETipoAmbiente } from '@/models/Enumeradores/PainelAdministrativo/ETipoAmbiente';
import { EStatusRetornoRequisicao, IRetornoRequisicao } from '@/core/models/IRetornoRequisicao';
import { ERelacaoArquivo } from '@/models/Enumeradores/MeuSistema/ERelacaoArquivo';

export default defineComponent({
  name: 'BancoModal',
  props: {
    visivel: {
      type: Boolean,
    },
    operacao: {
      type: Object as () => ITelaOperacao,
      required: true,
    },
    bancoCuradoria: {
      type: Object as () => IBanco,
    },
  },
  components: {
    RequisicaoModal,
    Card,
    Icone,
  },
  emits: ['sincronizarRegistro', 'acaoCuradoria', 'update:operacao', 'update:visivel'],
  setup(props, { emit }) {
    const {
      telaBase, obterPermissoes,
      preencherPermissoesDados, filtrarPermissaoDadosUsuario, apresentarMensagemSucesso, apresentarMensagemErro,
    } = useTelaBase();
    const {
      modalBase, apresentarRetornoRequisicao, apresentarBarraProgresso, ocultarBarraProgresso,
      defineNovaOperacao, defineTextoPadraoBotoes, sincronizarRegistro,
    } = useModalBase(props, emit);

    telaBase.identificadorRecurso = 'CADASTRO_BANCOS';
    telaBase.identificadorPermissao = 'PER_CADASTRO_BANCOS';

    const servicoBanco = new ServicoBanco();
    const servicoSistema = new ServicoSistema();
    const servicoPAS = new ServicoPAS();
    const state = reactive({
      banco: {} as IBanco,
      enviandoLogo: false,
      excluindoLogo: false,
      aplicarRegrasEnvioCuradoria: false,
      aplicarRegrasRevisaoCuradoria: false,
    });

    function objetoInicial() {
      state.banco = {} as IBanco;
      state.banco.ativo = true;
      state.aplicarRegrasEnvioCuradoria = false;
      state.aplicarRegrasRevisaoCuradoria = false;
    }

    function selecionarLogoUpload() {
      const elemento = document.getElementById('selecionarLogoBanco');
      if (elemento !== null) {
        elemento.click();
      }
    }

    async function enviarImagens(event:any) {
      state.enviandoLogo = true;
      const retornoLogo = await servicoSistema.enviarArquivoUnicoPublico(storeSistema.getters.codigoEmpresaOperacao(), ERelacaoArquivo.Sistema, event.target.files[0]);
      if (retornoLogo.status) {
        state.banco.logo = retornoLogo.linkArquivo;
      }
      state.enviandoLogo = false;
    }

    async function dropImagem(event:any) {
      state.enviandoLogo = true;

      const retornoLogo = await servicoSistema.enviarArquivoUnicoPublico(storeSistema.getters.codigoEmpresaOperacao(), ERelacaoArquivo.Sistema, event.dataTransfer.files[0]);
      if (retornoLogo.status) {
        state.banco.logo = retornoLogo.linkArquivo;
      }
      state.enviandoLogo = false;
    }

    async function excluirLogo() {
      state.excluindoLogo = true;
      const retornoArquivo = await servicoSistema.removerArquivoPorUrl(state.banco.logo);
      if (retornoArquivo.status !== EStatusRetornoRequisicao.Sucesso) {
        apresentarMensagemErro(retornoArquivo.mensagem);
      } else {
        state.banco.logo = '';
      }
      state.excluindoLogo = false;
    }

    function aprovarCadastro() {
      emit('acaoCuradoria', EStatusCuradoriaCadastro.Aprovado, state.banco);
      modalBase.computedVisivel = false;
    }

    function reprovarCadastro() {
      emit('acaoCuradoria', EStatusCuradoriaCadastro.Reprovado, state.banco);
      modalBase.computedVisivel = false;
    }

    async function salvar(salvarNovo: boolean) {
      let retorno: IRetornoRequisicao = { codigoRegistro: 0, status: 0, mensagem: '' };

      apresentarBarraProgresso();

      if (storeSistema.state.configuracaoApp.tipoAmbiente === ETipoAmbiente.Administracao) {
        if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
          retorno = await servicoBanco.incluir(state.banco);
        } else if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
          retorno = await servicoBanco.alterar(state.banco);
        }

        ocultarBarraProgresso();

        if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
          if (props.operacao.tipoPermissaoDados === EPermissaoDados.Incluir) {
            sincronizarRegistro(EPermissaoDados.Incluir, retorno.codigoRegistro);
          } else {
            sincronizarRegistro(EPermissaoDados.Alterar, props.operacao.codigoRegistro);
          }
          apresentarMensagemSucesso(retorno.mensagem);
          if (salvarNovo) {
            objetoInicial();
            defineNovaOperacao(props.operacao);
          } else {
            modalBase.computedVisivel = false;
          }
        } else {
          apresentarRetornoRequisicao(retorno);
        }
      } else if (storeSistema.state.configuracaoApp.tipoAmbiente === ETipoAmbiente.Plataforma) {
        retorno = await servicoPAS.solicitarCadastroCuradoria(telaBase.identificadorRecurso, state.banco);

        ocultarBarraProgresso();

        if (retorno.status === EStatusRetornoRequisicao.Sucesso) {
          apresentarMensagemSucesso(retorno.mensagem);
          modalBase.computedVisivel = false;
        } else {
          apresentarRetornoRequisicao(retorno);
        }
      }
    }

    watch(async () => modalBase.computedVisivel, async () => {
      telaBase.carregando = true;
      objetoInicial();
      if (modalBase.computedVisivel) {
        if (UtilitarioGeral.objetoValido(props.bancoCuradoria)) {
          state.aplicarRegrasRevisaoCuradoria = true;
          state.banco = UtilitarioGeral.instanciaObjetoLocal(props.bancoCuradoria);
        } else {
          if (props.operacao.listaPermissoesDados.length > 0) {
            await preencherPermissoesDados(props.operacao.listaPermissoesDados);
          } else {
            await obterPermissoes(ETipoPermissao.Dados);
          }
          telaBase.permissaoDados = filtrarPermissaoDadosUsuario(storeSistema.getters.codigoEmpresaOperacao());

          if (storeSistema.state.configuracaoApp.tipoAmbiente === ETipoAmbiente.Administracao) {
            state.aplicarRegrasEnvioCuradoria = false;
            defineTextoPadraoBotoes(props.operacao.tipoPermissaoDados);
          } else if (storeSistema.state.configuracaoApp.tipoAmbiente === ETipoAmbiente.Plataforma) {
            modalBase.textoBotaoSalvar = 'Sugerir Cadastro';
            state.aplicarRegrasEnvioCuradoria = true;
          }

          if (props.operacao.tipoPermissaoDados === EPermissaoDados.Visualizar) {
            state.banco = await servicoBanco.obter(props.operacao.codigoRegistro);
          }
        }
      }
      telaBase.carregando = false;
    });

    function apresentarLogoApenasPreenchidaNaPlataforma(): boolean {
      return (storeSistema.state.configuracaoApp.tipoAmbiente === ETipoAmbiente.Plataforma && UtilitarioGeral.valorValido(state.banco.logo));
    }

    function apresentarApenasAdministracao(): boolean {
      return (storeSistema.state.configuracaoApp.tipoAmbiente === ETipoAmbiente.Administracao);
    }

    return {
      telaBase,
      props,
      modalBase,
      EPermissaoDados,
      ETipoAmbiente,
      UtilitarioGeral,
      apresentarRetornoRequisicao,
      state,
      objetoInicial,
      selecionarLogoUpload,
      dropImagem,
      enviarImagens,
      excluirLogo,
      salvar,
      aprovarCadastro,
      reprovarCadastro,
      apresentarApenasAdministracao,
      apresentarLogoApenasPreenchidaNaPlataforma,
    };
  },
});
